import { useRef, useState } from "react";
import { useQueryClient, type QueryKey } from "@tanstack/react-query";
import { FileText, Paperclip, Trash2, Upload } from "lucide-react";
import apiClient from "@/api/client";
import { Button } from "@/components/ui/Button";
import { ManagedFileLink, type ManagedFileRef } from "@/components/ui/ManagedFileLink";
import type { DocumentAttachment } from "../types";
import { apiErrorText } from "../utils/apiErrorText";

interface Props {
  title?: string;
  attachments: DocumentAttachment[];
  /** Collection endpoint, e.g. `/protocols/{id}/attachments/`: POST adds, `{endpoint}{fileId}/` DELETE removes. */
  endpoint: string;
  canUpload: boolean;
  /** Queries that carry `attachments` — refreshed after every change. */
  invalidateKeys: QueryKey[];
  /** The protocol's generated PDF, listed first (read-only). */
  generatedPdf?: ManagedFileRef | null;
  hint?: string;
  /** Smaller heading, for use inside an expanded list row. */
  compact?: boolean;
  /** No adding, no removing — whatever the server's per-viewer flags say
   *  (the contractor's page, and GK's „Widok podwykonawcy" preview of it). */
  readOnly?: boolean;
}

/** Files of a whole POCK request / protocol — signed scan, correspondence, acceptance
 *  documentation — as opposed to the per-line attachments. */
export function DocumentAttachmentsPanel({
  title = "Dokumenty protokołu",
  attachments,
  endpoint,
  canUpload,
  invalidateKeys,
  generatedPdf,
  hint,
  compact = false,
  readOnly = false,
}: Props) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const refresh = () => invalidateKeys.forEach((queryKey) => qc.invalidateQueries({ queryKey }));

  const onFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (!files.length) return;
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    setBusy(true);
    setError("");
    try {
      await apiClient.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      refresh();
    } catch (err) {
      setError(apiErrorText(err, "Nie udało się dodać plików. Spróbuj ponownie."));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (att: DocumentAttachment) => {
    if (!window.confirm(`Usunąć plik „${att.original_name}” z dokumentu?`)) return;
    setBusy(true);
    setError("");
    try {
      await apiClient.delete(`${endpoint}${att.id}/`);
      refresh();
    } catch (err) {
      setError(apiErrorText(err, "Nie udało się usunąć pliku."));
    } finally {
      setBusy(false);
    }
  };

  const hasPdf = !!generatedPdf;
  const empty = attachments.length === 0 && !hasPdf;

  return (
    <section className={compact ? "mt-3" : "mb-6"} data-testid="document-attachments">
      <div className="flex items-center justify-between gap-3 mb-2">
        <h2
          className={`${compact ? "text-sm" : "text-base"} font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2`}
        >
          <Paperclip size={compact ? 14 : 16} className="text-gray-400 dark:text-gray-500" />
          {title}
        </h2>
        {canUpload && !readOnly && (
          <Button size="sm" variant="secondary" loading={busy} onClick={() => inputRef.current?.click()}>
            <Upload size={14} /> Dodaj pliki
          </Button>
        )}
      </div>
      <input ref={inputRef} type="file" multiple className="hidden" onChange={onFiles} data-testid="document-attachments-input" />
      {hint && <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{hint}</p>}
      {error && <p className="text-sm text-red-600 dark:text-red-400 mb-2">{error}</p>}
      {empty ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">Brak plików.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
          {hasPdf && (
            <li className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
              <ManagedFileLink
                file={generatedPdf}
                icon={<FileText size={12} />}
                empty={<span className="text-xs text-gray-400 dark:text-gray-500">PDF protokołu w przygotowaniu…</span>}
              >
                PDF protokołu (wygenerowany)
              </ManagedFileLink>
              <span className="text-xs text-gray-400 dark:text-gray-500">system</span>
            </li>
          )}
          {attachments.map((att) => (
            <li key={att.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
              <ManagedFileLink
                file={att.managed_file_detail}
                icon={<Paperclip size={12} />}
                className="max-w-full break-all"
                empty={<span className="text-xs text-gray-400 dark:text-gray-500">{att.original_name} (plik niedostępny)</span>}
              >
                {att.original_name}
              </ManagedFileLink>
              <span className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                {att.uploaded_by_name || "—"} · {new Date(att.uploaded_at).toLocaleString("pl")}
                {att.can_delete && !readOnly && (
                  <button
                    type="button"
                    onClick={() => remove(att)}
                    disabled={busy}
                    className="text-gray-400 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50"
                    title="Usuń plik"
                    aria-label={`Usuń plik ${att.original_name}`}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
